import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { api } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import type { AdminUser } from "./UserList";

// 刪掉一個離職同事的帳號(業主 2026-09-21)。
//
// > 使用者裡面 沒有刪除 可能這員工離職了 我們除了停用之外 還要刪除!
//
// ==========================================================================
// 「刪掉帳號」不等於「刪掉那個人的紀錄」
// ==========================================================================
// `users` 底下有六條外鍵是 CASCADE —— 真的把那一列刪掉,會連**出勤打卡**與
// **請假紀錄**一起帶走。出勤是勞基法要求保存五年的東西,而且是他領薪水的
// 唯一憑據;報價單的甲方承辦人(姓名/電話/EMAIL)也是從那一列即時推導的。
//
// 所以後端做的是「那一列不再是一個帳號」:登不進來、從每一支清單消失,
// 而姓名、Email、電話一個字都不動。
//
// **這個視窗的工作就是把上面那件事講給他聽。** 不講的話他不敢按 ——
// 而且他也判斷不了該不該按。

export interface UserDeleteDialogProps {
  /** 有值就是開著,而且是要刪這一個。 */
  user: AdminUser | null;
  onClose: () => void;
}

interface Impact {
  id: number;
  name: string;
  email: string;
  punches: number;
  leaves: number;
  quotes: number;
  expenses: number;
}

/** 會留下來的那幾樣。**數字是 0 的也要列** —— 「沒有」本身就是他要知道的事。 */
const KEPT: { key: keyof Impact; label: string }[] = [
  { key: "punches", label: "出勤打卡" },
  { key: "leaves", label: "請假 / 特休" },
  { key: "quotes", label: "他負責的報價單" },
  { key: "expenses", label: "他登錄的雜支" },
];

export function UserDeleteDialog({ user, onClose }: UserDeleteDialogProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [typed, setTyped] = useState("");
  const [failed, setFailed] = useState("");

  const open = user !== null;

  // 每次重開都從頭來 —— 上一個打過的字不可以留給下一個。
  useEffect(() => {
    if (!open) return;
    setTyped("");
    setFailed("");
  }, [open, user?.id]);

  const { data: impact, isLoading } = useQuery({
    queryKey: ["user-impact", user?.id],
    queryFn: () => api.get<Impact>(`/users/${user!.id}/impact`),
    enabled: open,
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/users/${user!.id}`, { email: typed.trim() }),
    onSuccess: () => {
      toast.push(`帳號「${user!.name || user!.email}」已經刪掉了`, "success");
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (err: unknown) => {
      // **視窗不關。** 最常見的失敗是 Email 打錯,而他要做的是再打一次。
      setFailed(err instanceof Error ? err.message : "刪不掉");
    },
  });

  if (!user) return null;

  const ready = typed.trim().toLowerCase() === (user.email || "").toLowerCase();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`刪除帳號「${user.name || user.email}」`}
      maxWidth="max-w-xl"
      // 破壞性的確認不給「點背景就關」—— 手滑點到旁邊就得從頭來一次。
      closeOnBackdrop={false}
      footer={
        <>
          <Button type="button" onClick={onClose}>
            取消
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={!ready}
            loading={remove.isPending}
            onClick={() => remove.mutate()}
            icon={<Trash2 size={15} aria-hidden="true" />}
          >
            確定刪除
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm text-[var(--text)]">
          刪掉之後他<strong>登不進來</strong>,而且從使用者清單、指派下拉、
          @ 提及與出席者選單裡完全消失。
        </p>

        {isLoading ? (
          <p className="m-0 text-sm text-[var(--text-3)]">查牽連中…</p>
        ) : null}

        {impact ? (
          <>
            <p className="m-0 text-sm text-[var(--text)]">
              下面這些<strong>全部留著</strong>,一筆都不會消失:
            </p>
            <ul className="m-0 flex list-none flex-col gap-1 p-0 pl-1">
              {KEPT.map((one) => (
                <li key={one.key} className="text-sm text-[var(--text-2)]">
                  {one.label}
                  <span className="tabular ml-2 font-medium text-[var(--text)]">
                    {impact[one.key]}
                  </span>
                  <span className="ml-1 text-[var(--text-3)]">筆</span>
                </li>
              ))}
            </ul>
            {/* 這一段是整個視窗最重要的一句話 —— 他要知道自己沒有在毀證據。 */}
            <p className="m-0 text-sm text-[var(--text-2)]">
              出勤與請假是<strong>法定要保存</strong>的(勞基法要求出勤紀錄留五年),
              而且是他領薪水的憑據;他負責過的報價單上,甲方承辦人的姓名、電話與
              EMAIL 也照樣印得出來 —— 那幾欄就是從這個帳號推導的。
            </p>
            <p className="m-0 text-sm text-[var(--text-3)]">
              也因為這樣,<strong>這個 Email 不會釋放出來</strong>:
              要給新人用的話請換一個。
            </p>
          </>
        ) : null}

        <p className="m-0 text-sm text-[var(--danger)]">
          <strong>刪除沒有回頭路</strong> —— 不會進回收桶。
        </p>

        {/* 打一次 Email:一排姓名很像的帳號,「確定嗎」問不出他要刪哪一個。 */}
        <Input
          label={`請打一次他的 Email 確認:${user.email}`}
          value={typed}
          autoComplete="off"
          onChange={(e) => setTyped(e.target.value)}
        />

        {failed ? (
          <p role="alert" className="m-0 text-sm text-[var(--danger)]">
            {failed}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
